import React from 'react';

import { ButtonGroup, forwardRef, rem, StyleProps, useBreakpointValue } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { useActionInput } from '../context';
import { ActionInputContextType } from '../types';
import { CollapsedButtons } from './decorations/collapsed-buttons';
import { GiftWrapButton } from './decorations/gift-wrap-button';
import { useDecorationButtons } from './decorations/hooks/use-decoration-buttons';

const VIDEO_MODULE = 'video-recording';
const AUDIO_MODULE = 'audio-recording';

export const ActionDecorations = forwardRef<StyleProps, 'div'>((props: StyleProps, ref) => {
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const buttons = useDecorationButtons();
	const company = useCurrentCompany();
	const { context } = useActionInput();
	const isGiftWrapEnabled = !!company.post_wrap_enabled;
	const showGiftWrap = isGiftWrapEnabled && context === ActionInputContextType.Recognition;

	if (!buttons.length) {
		return null;
	}

	const videoButton = buttons.find((button) => button.key === VIDEO_MODULE);
	const audioButton = buttons.find((button) => button.key === AUDIO_MODULE);

	if (isMobile) {
		const mobileCollapsedButtons = buttons
			.filter((button) => ['link', 'mention', 'gif', 'attachment', 'video-recording', 'audio-recording'].includes(button.key as string))
			.sort((a, b) => {
				const order = ['video-recording', 'audio-recording', 'gif', 'attachment', 'link', 'mention'];
				return order.indexOf(a.key as string) - order.indexOf(b.key as string);
			})
			.map((button) => React.cloneElement(button, { decorationVariant: 'list-item' }));

		const allMobileCollapsedButtons = [...(showGiftWrap ? [<GiftWrapButton key="gift-wrap" decorationVariant="list-item" />] : []), ...mobileCollapsedButtons];

		return (
			<ButtonGroup isAttached p={rem(1)} variant="ghost" ref={ref} {...props}>
				<CollapsedButtons buttons={allMobileCollapsedButtons} />
			</ButtonGroup>
		);
	}

	// Redesigned order: video, audio, gif, upload, emoji, then collapsed (link, mention)
	const gifButton = buttons.find((button) => button.key === 'gif');
	const attachmentButton = buttons.find((button) => button.key === 'attachment');
	const imageButton = buttons.find((button) => button.key === 'image');
	const uploadButton = attachmentButton || imageButton;
	const emojiButton = buttons.find((button) => button.key === 'emoji');

	// Buttons to collapse: link and mention
	const collapsedRedesign = buttons.filter((button) => button.key === 'link' || button.key === 'mention');

	// Free rewards: audio moves to collapsed, gift wrap is the last visible button before collapsed
	if (isGiftWrapEnabled) {
		const collapsedWithAudio = [...collapsedRedesign, ...(audioButton ? [audioButton] : [])].map((button) =>
			React.cloneElement(button, { decorationVariant: 'list-item' })
		);

		return (
			<ButtonGroup isAttached p={rem(1)} ref={ref} variant="ghost" {...props}>
				{videoButton}
				{gifButton}
				{uploadButton}
				{emojiButton}
				{showGiftWrap && <GiftWrapButton />}
				{collapsedWithAudio.length > 0 && <CollapsedButtons buttons={collapsedWithAudio} />}
			</ButtonGroup>
		);
	}

	const collapsedButtonsList = collapsedRedesign.map((button) => React.cloneElement(button, { decorationVariant: 'list-item' }));

	return (
		<ButtonGroup isAttached p={rem(1)} ref={ref} variant="ghost" {...props}>
			{videoButton}
			{audioButton}
			{gifButton}
			{uploadButton}
			{emojiButton}
			{collapsedButtonsList.length > 0 && <CollapsedButtons buttons={collapsedButtonsList} />}
		</ButtonGroup>
	);
});

ActionDecorations.displayName = 'ActionDecorations';
